'use client'

import { X24 } from '@vinted/monochrome-icons'
import {
  Bubble,
  Button,
  Container,
  Dialog,
  Divider,
  Image,
  Navigation,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { noop } from 'lodash'
import { useEffect, useState, useContext } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSystemConfiguration } from '@marketplace-web/shared/system-configuration-data'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { useAsset } from '@marketplace-web/shared/assets'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  deleteShippingOrderContact,
  getShippingContactConfiguration,
  saveShippingOrderContact,
  ShippingContactType,
  transformShippingContactConfigurationResp,
} from '@marketplace-web/shipping/shipping-contact-data'
import {
  deleteTransactionShippingContact,
  saveTransactionShippingContact,
} from '@marketplace-web/address/address-data'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { GetShippingLabelContext } from '@marketplace-web/shipping-services-provider/shipping-services-provider-feature'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'

import { clickEvent } from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { buyerViewCheckoutEvent } from '@marketplace-web/checkout/checkout-data'
import { useAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { PhoneNumber } from './Fields'

type Props = {
  isOpen: boolean
  transactionId: number | null
  type: ShippingContactType
  phoneNumber: string | null | undefined
  shippingOrderId?: number | null
  shipmentId?: number | null
  onClose: () => void
  setShippingContactNumber?: (phoneNumber: string | null) => void
}

const ShippingContactModal = ({
  isOpen,
  type,
  transactionId,
  phoneNumber,
  shippingOrderId,
  shipmentId,
  onClose,
  setShippingContactNumber = noop,
}: Props) => {
  const { track } = useTracking()
  const translate = useTranslate(`shipping_contact.${type}.form`)
  const asset = useAsset('/assets/checkout')
  const phoneNumberPrefix = useSystemConfiguration()?.phoneNumberPrefix
  const isLabelOptionsRewriteFSEnabled = useFeatureSwitch('web_label_options_rewrite')
  const dropOffTypeAndPackagingRewrite = useAbTest('dd_drop_off_type_and_packaging_rewrite')
  const isDropOffTypeAndPackagingRewriteOn = dropOffTypeAndPackagingRewrite?.variant === 'on'

  const currentPhoneNumber = phoneNumber || phoneNumberPrefix

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [persistPhoneNumber, setPersistPhoneNumber] = useState(false)
  const [inputPhoneNumber, setInputPhoneNumber] = useState(currentPhoneNumber)
  const [error, setError] = useState<string | null | undefined>(undefined)
  const [uiState, setUiState] = useState(UiState.Idle)

  const {
    state: {
      labelOptions: { fetchLabelOptions },
    },
  } = useContext(GetShippingLabelContext)

  const {
    transformedData: shippingContactConfiguration,
    fetch: fetchGetShippingContactConfiguration,
    isLoading: isShippingContactConfigurationLoading,
  } = useFetch(getShippingContactConfiguration, transformShippingContactConfigurationResp)

  const isCompleted = !!phoneNumber

  useEffect(() => {
    if (!transactionId) return
    if (!isOpen) return

    const trackModalOpening = () => {
      if (type === ShippingContactType.Buyer) {
        track(buyerViewCheckoutEvent({ transactionId, screen: 'contact_details' }))

        return
      }

      track(
        clickEvent({
          screen: 'get_shipping_label',
          target: 'seller_add_contact_details',
          targetDetails: JSON.stringify({ transactionId }),
        }),
      )
    }

    trackModalOpening()
  }, [isOpen, transactionId, track, type])

  useEffect(() => {
    if (isOpen) return

    setError(null)
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return

    fetchGetShippingContactConfiguration()
  }, [fetchGetShippingContactConfiguration, isOpen])

  useEffect(() => {
    setInputPhoneNumber(currentPhoneNumber)
  }, [currentPhoneNumber])

  const getTrackingScreen = () => {
    return type === ShippingContactType.Buyer ? 'contact_details' : 'seller_contact_details'
  }

  const trackPersistPhoneNumber = () => {
    track(
      clickEvent({
        screen: getTrackingScreen(),
        target: 'contact_details_continue',
        targetDetails: JSON.stringify({
          remember_phone_number: persistPhoneNumber,
          transaction_id: transactionId,
        }),
      }),
    )
  }

  const trackClick = (target: Parameters<typeof clickEvent>[0]['target']) => {
    track(clickEvent({ screen: getTrackingScreen(), target }))
  }

  const handleContinueClick = async (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (uiState === UiState.Pending) {
      event.preventDefault()

      return
    }

    if (!transactionId || error) return

    setUiState(UiState.Pending)

    const buyerPhoneNumber = type === ShippingContactType.Buyer ? inputPhoneNumber : null
    const sellerPhoneNumber = type === ShippingContactType.Seller ? inputPhoneNumber : null

    const response = shippingOrderId
      ? await saveShippingOrderContact({
          shippingOrderId: shippingOrderId.toString(),
          saveForLater: persistPhoneNumber,
          phoneNumber: inputPhoneNumber ?? null,
        })
      : await saveTransactionShippingContact({
          transactionId,
          buyerPhoneNumber: buyerPhoneNumber ?? null,
          sellerPhoneNumber: sellerPhoneNumber ?? null,
          saveForLater: persistPhoneNumber,
        })

    if ('errors' in response) {
      if (response.errors.length) {
        setError(response.errors[0]?.value)
      }

      setUiState(UiState.Failure)

      return
    }

    setShippingContactNumber(inputPhoneNumber || null)

    setError(null)
    setUiState(UiState.Success)
    setPersistPhoneNumber(false)
    trackPersistPhoneNumber()
    if (shipmentId)
      fetchLabelOptions({
        shipmentId,
        isLabelOptionsRewriteFSEnabled:
          isDropOffTypeAndPackagingRewriteOn && isLabelOptionsRewriteFSEnabled,
      })
    onClose()
  }

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true)
    trackClick('delete_contact_details')
  }

  const handleDeleteShippingContact = async (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => {
    if (uiState === UiState.Pending) {
      event.preventDefault()

      return
    }

    if (!transactionId) return

    setUiState(UiState.Pending)

    const response = shippingOrderId
      ? await deleteShippingOrderContact(shippingOrderId.toString())
      : await deleteTransactionShippingContact(transactionId)

    if ('errors' in response) {
      if (response.errors.length) {
        setError(response.errors[0]?.value)
      }

      setUiState(UiState.Failure)

      return
    }

    setShippingContactNumber(null)
    setError(null)
    setUiState(UiState.Success)
    setInputPhoneNumber(phoneNumberPrefix)
    trackClick('confirm_phone_number_deletion')
    setIsDeleteModalOpen(false)
    if (shipmentId)
      fetchLabelOptions({
        shipmentId,
        isLabelOptionsRewriteFSEnabled:
          isDropOffTypeAndPackagingRewriteOn && isLabelOptionsRewriteFSEnabled,
      })
    onClose()
  }

  const handleCancelDeleteShippingContact = () => {
    setIsDeleteModalOpen(false)
  }

  const handlePhoneNumberFieldBlur = (value: string) => {
    setInputPhoneNumber(value)
  }

  const handlePhoneNumberPersist = () => {
    setPersistPhoneNumber(previous => !previous)
  }

  const handleModalClose = () => {
    onClose()
    setInputPhoneNumber(currentPhoneNumber)
    setPersistPhoneNumber(false)
  }

  const renderLoader = () => (
    <>
      <ContentLoader styling={ContentLoader.Styling.Wide} size={ContentLoader.Size.Large} />
      <Spacer size="large" />
    </>
  )

  const renderInformation = () => (
    <Bubble styling="default">
      <SeparatedList separator={<Spacer size="large" />}>
        <div className="u-flexbox">
          <Image src={asset('shipping_contact_notification_box.svg')} size="x2-large" />
        </div>
        <Text
          as="h1"
          type="heading"
          alignment="center"
          width="parent"
          text={translate('information.title')}
        />
        <Text as="span" alignment="center" width="parent" text={translate('information.message')} />
      </SeparatedList>
    </Bubble>
  )

  const renderButtons = () => {
    return (
      <>
        <Button
          text={translate('actions.continue')}
          styling="filled"
          disabled={uiState === UiState.Pending}
          isLoading={uiState === UiState.Pending}
          onClick={handleContinueClick}
          testId="shipping-contact-continue-button"
        />
        {isCompleted && (
          <>
            <Spacer size="large" />
            <Button
              text={translate('actions.delete')}
              theme="warning"
              disabled={uiState === UiState.Pending}
              onClick={handleDeleteClick}
              testId="shipping-contact-delete-button"
            />
          </>
        )}
      </>
    )
  }

  const renderDeleteModal = () => {
    if (!isDeleteModalOpen) return null

    return (
      <Dialog show={isDeleteModalOpen} className="shipping-contact-modal__delete-modal">
        <Container>
          <SeparatedList separator={<Spacer size="large" />}>
            <div className="u-flexbox u-justify-content-center">
              <Text as="h1" text={translate('delete_confirmation.title')} type="heading" />
            </div>
            <Text as="span" text={translate('delete_confirmation.message')} />
            <Button
              text={translate('delete_confirmation.actions.delete')}
              styling="filled"
              theme="warning"
              disabled={uiState === UiState.Pending}
              isLoading={uiState === UiState.Pending}
              onClick={handleDeleteShippingContact}
              testId="shipping-contact-delete-confirm-button"
            />
            <Button
              text={translate('delete_confirmation.actions.cancel')}
              styling="flat"
              disabled={uiState === UiState.Pending}
              onClick={handleCancelDeleteShippingContact}
            />
          </SeparatedList>
        </Container>
      </Dialog>
    )
  }

  const renderContent = () => {
    if (isShippingContactConfigurationLoading) return renderLoader()

    return (
      <>
        <PhoneNumber
          transactionId={transactionId}
          validations={shippingContactConfiguration?.phoneNumber?.validations}
          value={inputPhoneNumber ?? null}
          persist={persistPhoneNumber}
          isCompleted={isCompleted}
          type={type}
          error={error}
          isLoading={uiState === UiState.Pending}
          setError={setError}
          onBlur={handlePhoneNumberFieldBlur}
          onPersist={handlePhoneNumberPersist}
          onInputChange={setInputPhoneNumber}
        />
        <Container>{renderButtons()}</Container>
      </>
    )
  }

  return (
    <Dialog show={isOpen} hasScrollableContent testId="shipping-contact-modal">
      <div className="u-fill-width">
        <Navigation
          body={translate('title')}
          right={
            <Button
              iconName={X24}
              styling="flat"
              testId="shipping-contact-modal-close"
              onClick={handleModalClose}
            />
          }
        />
        <Divider />
        <ScrollableArea>
          <Container>{renderInformation()}</Container>
          {renderContent()}
        </ScrollableArea>
      </div>
      {renderDeleteModal()}
    </Dialog>
  )
}

export default ShippingContactModal
